import { API_CONFIG } from '../config/api';
import { ApiValidationError } from '../types/auth';
import type {
  ItemCategory,
  CreateItemCategoryData,
  UpdateItemCategoryData,
  ItemCategoriesResponse,
  ItemCategoryResponse,
} from '../types/itemCategory';

const authHeaders = (): Record<string, string> => ({
  Accept: 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const parseErrorResponse = async (response: Response, fallback: string) => {
  const errorData = await response.json().catch(() => ({}));
  const message = errorData.message || fallback;

  if (response.status === 422 && errorData.errors) {
    throw new ApiValidationError(message, errorData.errors);
  }

  throw new Error(message);
};

export const getAllItemCategories = async (params?: {
  tree?: boolean;
  parent_id?: number | null;
  active?: boolean;
}): Promise<ItemCategory[]> => {
  const searchParams = new URLSearchParams();
  if (params?.tree) searchParams.set('tree', '1');
  if (params?.parent_id === null) searchParams.set('parent_id', 'null');
  else if (params?.parent_id != null) searchParams.set('parent_id', String(params.parent_id));
  if (params?.active != null) searchParams.set('active', params.active ? '1' : '0');

  const query = searchParams.toString();
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.ITEM_CATEGORIES.GET_ALL}${
    query ? `?${query}` : ''
  }`;

  const response = await fetch(url, {
    method: 'GET',
    headers: authHeaders(),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to fetch item categories');
  }

  const result: ItemCategoriesResponse = await response.json();
  return result.data ?? [];
};

export const createItemCategory = async (data: CreateItemCategoryData): Promise<ItemCategory> => {
  const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.ITEM_CATEGORIES.CREATE}`, {
    method: 'POST',
    headers: {
      ...authHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to create item category');
  }

  const result: ItemCategoryResponse = await response.json();
  return result.data;
};

export const updateItemCategory = async (
  categoryId: number,
  data: UpdateItemCategoryData
): Promise<ItemCategory> => {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.ITEM_CATEGORIES.UPDATE}/${categoryId}`,
    {
      method: 'PATCH',
      headers: {
        ...authHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    }
  );

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to update item category');
  }

  const result: ItemCategoryResponse = await response.json();
  return result.data;
};

export const deleteItemCategory = async (categoryId: number): Promise<void> => {
  const response = await fetch(
    `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.ITEM_CATEGORIES.DELETE}/${categoryId}`,
    {
      method: 'DELETE',
      headers: authHeaders(),
    }
  );

  if (!response.ok) {
    await parseErrorResponse(response, 'Failed to delete item category');
  }
};
